/**
 * Portable pipeline (import / export).
 *
 * A pipeline references its stages, and stages reference each other (and the
 * interactors that segue between them) by numeric ID. Those IDs are local to a
 * single environment, so a pipeline exported from one environment (e.g. Live)
 * cannot simply be POSTed to another (e.g. Beta) — the IDs would not match.
 *
 * This module captures the portable shape of a pipeline (no environment
 * specific runtime fields) and the pure helpers used to remap those references
 * when the pipeline is re-materialized in another environment. The remapping
 * mirrors the backend `DuplicatePipelineByID` logic (see mercurykit
 * `AdjustStageAvailableStageIDs` / `AdjustStageExcludedStageIDs`): numeric
 * references that cannot be mapped are dropped, while the `*` wildcard is kept.
 */

import { Interactor_Entity } from "../interactor";
import { Stage_Entity } from "../stage";
import Pipeline_Entity from "./pipeline";

export const PORTABLE_PIPELINE_KIND = "rhapsody.pipeline";
export const PORTABLE_PIPELINE_VERSION = 1;

/** Interactor fields that are safe to carry across environments. */
export type PortableInteractor = Pick<
  Interactor_Entity,
  | "id"
  | "name"
  | "keyName"
  | "icon"
  | "color"
  | "mandatory"
  | "title"
  | "description"
  | "kind"
  | "segueID"
  | "data"
  | "order"
  | "acknowledgment"
  | "config"
>;

/** Stage fields that are safe to carry across environments. */
export type PortableStage = Pick<
  Stage_Entity,
  | "id"
  | "name"
  | "keyName"
  | "icon"
  | "color"
  | "primary"
  | "terminus"
  | "messaging"
  | "manual"
  | "nudgeable"
  | "silent"
  | "available"
  | "excluded"
  | "position"
  | "config"
  | "acknowledgmentRequired"
> & { interactors: PortableInteractor[] };

/** Pipeline fields that are safe to carry across environments. */
export type PortablePipelineBody = Pick<
  Pipeline_Entity,
  | "name"
  | "organizationID"
  | "public"
  | "inherit"
  | "excludedStageIDs"
  | "excludedInteractorIDs"
> & { stages: PortableStage[] };

export interface PortablePipeline {
  kind: typeof PORTABLE_PIPELINE_KIND;
  version: number;
  exportedAt: string;
  pipeline: PortablePipelineBody;
}

/**
 * Build the portable representation of a pipeline.
 *
 * Only the pipeline's own stages are exported. Inherited stages belong to other
 * pipelines and are carried over via the `inherit` reference instead — exporting
 * them would wrongly duplicate them on import.
 *
 * @param pipeline The fully loaded pipeline (stages + nested interactors).
 * @returns {PortablePipeline} The portable envelope.
 */
export function buildPortablePipeline(
  pipeline: Pipeline_Entity
): PortablePipeline {
  const ownStages = (pipeline.stages ?? []).filter(
    (s) => s.pipelineID === pipeline.id
  );

  const stages: PortableStage[] = ownStages.map((s) => ({
    id: s.id,
    name: s.name,
    keyName: s.keyName,
    icon: s.icon,
    color: s.color,
    primary: s.primary,
    terminus: s.terminus,
    messaging: s.messaging,
    manual: s.manual,
    nudgeable: s.nudgeable,
    silent: s.silent,
    available: s.available,
    excluded: s.excluded,
    position: s.position,
    config: s.config,
    acknowledgmentRequired: s.acknowledgmentRequired,
    interactors: (s.interactors ?? []).map((i) => ({
      id: i.id,
      name: i.name,
      keyName: i.keyName,
      icon: i.icon,
      color: i.color,
      mandatory: i.mandatory,
      title: i.title,
      description: i.description,
      kind: i.kind,
      segueID: i.segueID,
      data: i.data,
      order: i.order,
      acknowledgment: i.acknowledgment,
      config: i.config,
    })),
  }));

  return {
    kind: PORTABLE_PIPELINE_KIND,
    version: PORTABLE_PIPELINE_VERSION,
    exportedAt: new Date().toISOString(),
    pipeline: {
      name: pipeline.name,
      organizationID: pipeline.organizationID,
      public: pipeline.public,
      inherit: pipeline.inherit,
      excludedStageIDs: pipeline.excludedStageIDs,
      excludedInteractorIDs: pipeline.excludedInteractorIDs,
      stages,
    },
  };
}

/**
 * Parse and validate a portable pipeline file.
 *
 * Accepts both the wrapped envelope produced by {@link buildPortablePipeline}
 * and a bare pipeline object that exposes a `stages` array, so hand-edited or
 * older exports remain importable.
 *
 * @param raw The raw file contents.
 * @returns {PortablePipeline} The normalized envelope.
 * @throws If the contents are not valid JSON or are not a pipeline.
 */
export function parsePortablePipeline(raw: string): PortablePipeline {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch (e) {
    throw new Error("The selected file is not valid JSON.");
  }

  const root = json as Record<string, unknown> | null;
  const body = (root?.pipeline ?? root) as PortablePipelineBody | undefined;

  if (!body || typeof body !== "object" || !Array.isArray(body.stages)) {
    throw new Error(
      "This file does not look like an exported pipeline (no stages found)."
    );
  }

  // Import remapping keys every stage/interactor/routing reference off the
  // stage's numeric id. A stage without an id would be imported with its
  // interactors and routing silently dropped, so reject such files outright.
  const stageMissingId = body.stages.some(
    (s) => s == null || typeof s.id !== "number"
  );
  if (stageMissingId) {
    throw new Error(
      "This pipeline export is missing stage ids and cannot be imported safely. Re-export it from the source environment."
    );
  }

  return {
    kind: PORTABLE_PIPELINE_KIND,
    version:
      typeof root?.version === "number"
        ? (root.version as number)
        : PORTABLE_PIPELINE_VERSION,
    exportedAt:
      typeof root?.exportedAt === "string" ? (root.exportedAt as string) : "",
    pipeline: body,
  };
}

/**
 * Trigger a browser download of a portable pipeline as a JSON file.
 *
 * @param portable The portable envelope to download.
 */
export function downloadPortablePipeline(portable: PortablePipeline): void {
  const name = portable.pipeline.name ?? "pipeline";
  const safeName =
    name.replace(/[^a-z0-9-_]+/gi, "-").toLowerCase() || "pipeline";
  const blob = new Blob([JSON.stringify(portable, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `pipeline-${safeName}.json`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/**
 * Remap a comma-separated list of stage IDs against an old -> new ID map.
 *
 * Mirrors the backend duplicate logic: the `*` wildcard is preserved, numeric
 * references present in the map are translated, and references that cannot be
 * mapped (e.g. stages from inherited pipelines that do not exist in the target
 * environment) are dropped.
 *
 * @param csv The original `available` / `excluded` value.
 * @param idMap Old stage ID -> new stage ID.
 * @returns {string} The remapped, comma-separated value.
 */
export function remapStageIDsCSV(
  csv: string | undefined | null,
  idMap: Record<number, number>
): string {
  if (!csv) return "";

  const out: string[] = [];
  csv
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((part) => {
      if (part === "*") {
        out.push("*");
        return;
      }
      const oldID = parseInt(part, 10);
      if (!Number.isNaN(oldID) && idMap[oldID] != null) {
        out.push(`${idMap[oldID]}`);
      }
    });

  return out.join(",");
}
